import { getAuthBaseUrl } from '../net/api-base';
import { applyBackendLogin } from '../net/session';
import { workerFetch } from '../net/network-client';

export class AuthApiError extends Error {
  status: number;
  body: unknown;

  constructor(message: string, { status, body }: { status: number; body?: unknown } = { status: 0 }) {
    super(message);
    this.name = 'AuthApiError';
    this.status = status;
    this.body = body;
  }
}

export { getAuthBaseUrl } from '../net/api-base';

async function authRequest<T = unknown>(path: string, options: RequestInit = {}) {
  const url = `${getAuthBaseUrl()}${path}`;
  const res = await workerFetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    },
  });

  const body = res.status === 204 ? null : await res.json().catch(() => null);

  if (!res.ok) {
    const errBody = body as { message?: string; error?: string } | null;
    throw new AuthApiError(errBody?.message || errBody?.error || res.statusText, {
      status: res.status,
      body,
    });
  }

  return body as T;
}

export async function fetchAuthMeta() {
  return authRequest<{ gaupaHost: string; publicKey: string }>('/meta');
}

export async function exchangeChallenge(challenge: string) {
  const login = await authRequest('/session', {
    method: 'POST',
    body: JSON.stringify({ challenge }),
  });
  applyBackendLogin(login as Parameters<typeof applyBackendLogin>[0]);
  return login;
}

export async function sendEmailCode(email: string) {
  return authRequest<{ sessionId: string; message?: string }>('/email/send', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function verifyEmailCode(sessionId: string, code: string) {
  const login = await authRequest('/email/verify', {
    method: 'POST',
    body: JSON.stringify({ sessionId, code }),
  });
  applyBackendLogin(login as Parameters<typeof applyBackendLogin>[0]);
  return login;
}

export async function fetchTelegramNonce(telegramId?: string) {
  const body = telegramId ? { telegramId } : {};
  return authRequest<{ nonce: string; launchId: string }>('/telegram/nonce', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function loginTelegram({
  initData,
  nonce,
  launchId,
}: {
  initData: string;
  nonce: string;
  launchId: string;
}) {
  const login = await authRequest('/telegram', {
    method: 'POST',
    body: JSON.stringify({ initData, nonce, launchId }),
  });
  applyBackendLogin(login as Parameters<typeof applyBackendLogin>[0]);
  return login;
}
